import React from "react";
import { Link } from "react-router-dom";
import { useLang } from "../i18n";
import Seo from "../components/Seo";
import { Eyebrow, Reveal, GoldButton } from "../components/motion";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "../components/ui/accordion";

const Shell = ({ eyebrow, title, sub, children, testid }) => (
  <div className="pt-[72px]" data-testid={testid}>
    <div className="max-w-[1100px] mx-auto px-4 sm:px-8 py-16 md:py-24">
      <Reveal>
        <Eyebrow>{eyebrow}</Eyebrow>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-ivory mb-4">{title}</h1>
        {sub && <p className="text-neutral-400 max-w-2xl mb-14">{sub}</p>}
      </Reveal>
      {children}
    </div>
  </div>
);

export function HowItWorks() {
  const { t, lang } = useLang();
  return (
    <>
      <Seo title={`${t("how.title")} — Chef à Domicile`} description={t("how.sub")} />
      <Shell eyebrow="Chef à Domicile" title={t("how.title")} sub={t("how.sub")} testid="how-it-works-page">
        <div className="grid md:grid-cols-2 gap-px bg-white/8 border border-white/8">
          {[1, 2, 3, 4].map((n, i) => (
            <Reveal key={n} delay={i * 0.06}>
              <div className="bg-ink p-10 md:p-14 h-full">
                <h2 className="font-serif text-xl sm:text-2xl text-gold mb-4">{t(`how.s${n}t`)}</h2>
                <p className="text-neutral-400 leading-relaxed">{t(`how.s${n}b`)}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal className="text-center mt-16">
          <Link to={`/${lang}/chefs`}><GoldButton data-testid="how-cta-btn">{t("nav.findChef")}</GoldButton></Link>
        </Reveal>
      </Shell>
    </>
  );
}

export function About() {
  const { t } = useLang();
  return (
    <>
      <Seo title={`${t("about.title")} — Chef à Domicile`} description={t("about.p1").slice(0, 155)} />
      <Shell eyebrow="Chef à Domicile" title={t("about.title")} testid="about-page">
        <div className="grid lg:grid-cols-2 gap-14 items-start">
          <div className="space-y-6">
            {[1, 2, 3].map((n) => (
              <Reveal key={n}><p className="text-neutral-300 leading-relaxed text-lg">{t(`about.p${n}`)}</p></Reveal>
            ))}
          </div>
          <Reveal>
            <img src="https://images.pexels.com/photos/4253298/pexels-photo-4253298.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="Chef" loading="lazy" className="w-full aspect-[4/5] object-cover" />
          </Reveal>
        </div>
      </Shell>
    </>
  );
}

export function PrivateDining() {
  const { t, lang } = useLang();
  return (
    <>
      <Seo title={`${t("privateDining.title")} — Chef à Domicile`} description={t("privateDining.sub")} />
      <Shell eyebrow={t("nav.privateDining")} title={t("privateDining.title")} sub={t("privateDining.sub")} testid="private-dining-page">
        <Reveal>
          <img src="https://images.pexels.com/photos/39405349/pexels-photo-39405349.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=800&w=1600" alt="Private dining table" loading="lazy" className="w-full aspect-[21/9] object-cover mb-14" />
        </Reveal>
        <div className="grid md:grid-cols-2 gap-10 mb-16">
          <Reveal><p className="text-neutral-300 leading-relaxed">{t("privateDining.p1")}</p></Reveal>
          <Reveal delay={0.1}><p className="text-neutral-300 leading-relaxed">{t("privateDining.p2")}</p></Reveal>
        </div>
        <Reveal className="text-center">
          <Link to={`/${lang}/chefs`}><GoldButton data-testid="private-dining-cta">{t("privateDining.cta")}</GoldButton></Link>
        </Reveal>
      </Shell>
    </>
  );
}

export function Faq() {
  const { t } = useLang();
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [1, 2, 3, 4, 5].map((n) => ({
      "@type": "Question",
      name: t(`faq.q${n}`),
      acceptedAnswer: { "@type": "Answer", text: t(`faq.a${n}`) },
    })),
  };
  return (
    <>
      <Seo title={`${t("faq.title")} — Chef à Domicile`} description={t("faq.a1").slice(0, 155)} jsonLd={jsonLd} />
      <Shell eyebrow="FAQ" title={t("faq.title")} testid="faq-page">
        <Accordion type="single" collapsible className="border border-white/8 divide-y divide-white/8">
          {[1, 2, 3, 4, 5].map((n) => (
            <AccordionItem key={n} value={`q${n}`} className="border-0 px-6">
              <AccordionTrigger data-testid={`faq-trigger-${n}`} className="font-serif text-lg text-ivory hover:text-gold hover:no-underline py-6">
                {t(`faq.q${n}`)}
              </AccordionTrigger>
              <AccordionContent className="text-neutral-400 leading-relaxed pb-6">{t(`faq.a${n}`)}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </Shell>
    </>
  );
}

export function Legal({ page }) {
  const { t } = useLang();
  const titles = { terms: "legal.termsTitle", privacy: "legal.privacyTitle", cancellation: "legal.cancelTitle" };
  const keys = { terms: "terms", privacy: "privacy", cancellation: "cancel" };
  return (
    <>
      <Seo title={`${t(titles[page])} — Chef à Domicile`} description={t(`legal.${keys[page]}1`).slice(0, 155)} />
      <Shell eyebrow={t("footer.legal")} title={t(titles[page])} testid={`legal-${page}-page`}>
        <div className="space-y-8 max-w-3xl">
          {[1, 2, 3].map((n) => (
            <Reveal key={n}><p className="text-neutral-300 leading-relaxed">{t(`legal.${keys[page]}${n}`)}</p></Reveal>
          ))}
        </div>
      </Shell>
    </>
  );
}
